import log from "../log";
import { createParser } from "eventsource-parser";

const HEADER_TIMEOUT = 10_000;
const FIRST_TOKEN_TIMEOUT = 35_000;
const TOKEN_TIMEOUT = 30_000;
// Other implementations: https://github.com/search?q=eventsource-parser+chat+stream&type=code
// https://github.com/Bin-Huang/chatbox/blob/236f87bc0443e3f4f8599cf99d2384a19cbb181c/src/packages/llm.ts#L129

export async function handleSSE(
  response: Response,
  onMessage: (messageData: string) => void
) {
  if (!response.ok) {
    const error = await response.json().catch(() => null);

    // For 403 errors with display_message, prioritize showing that message
    if (response.status === 403 && error?.display_message) {
      throw new Error(
        JSON.stringify({
          headers: response.headers,
          body: error,
          status: response.status,
          display_message: error.display_message,
        })
      );
    }

    throw new Error(
      error
        ? JSON.stringify({
            headers: response.headers,
            body: error,
            status: response.status,
          })
        : `${response.status} ${response.statusText}`
    );
  }
  if (response.status !== 200) {
    throw new Error(
      `Error from OpenAI: ${response.status} ${response.statusText}`
    );
  }
  if (!response.body) {
    throw new Error("No response body");
  }

  const parser = createParser({
    onEvent: (event) => {
      // NOTE(liamvdv) event.id: string, event.event: string
      //                Not Used By Backend.
      onMessage(event.data);
    },
    onError: (error) => console.error("SSE error:", error),
    onComment: (comment) => console.warn("SSE comment:", comment),
    onRetry: (retry) => console.warn("SSE retry:", retry),
  });

  for await (const chunk of iterableStreamAsync(response.body)) {
    const str = new TextDecoder().decode(chunk);
    parser.feed(str);
  }
}

export async function* iterableStreamAsync(
  stream: ReadableStream
): AsyncIterableIterator<Uint8Array> {
  const reader = stream.getReader();
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        return;
      } else {
        yield value;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function SSE(
  url: string,
  options: {
    headers: Headers;
    payload: Record<string, any>;
    onData: (option: {
      headers: Headers;
      data: any;
      cancel: (r: any) => void;
    }) => void;
    onError: ({ error, signal }: { error: Error; signal: AbortSignal }) => void;
    onEnd: (status: string) => void;
  }
) {
  let status = "pending";
  let hasCancel = false;

  const controller = new AbortController();
  const cancel = (r: any) => {
    hasCancel = true;
    controller.abort(r);
  };

  const headersTimer = setTimeout(() => {
    cancel(new Error("Request headers took too long."));
  }, HEADER_TIMEOUT);
  let tokenTimer: ReturnType<typeof setTimeout> | undefined;

  const extendTokenTimer = (ms: number) => {
    if (tokenTimer) clearTimeout(tokenTimer);
    console.log("extended timer");
    tokenTimer = setTimeout(() => {
      cancel(new Error("Time to next tokens is too long."));
    }, ms);
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: [...options.headers, ["Content-Type", "application/json"]],
      body: JSON.stringify(options.payload),
      signal: controller.signal,
    });
    // clear header timeout
    clearTimeout(headersTimer);
    extendTokenTimer(FIRST_TOKEN_TIMEOUT);

    await handleSSE(response, (message) => {
      extendTokenTimer(TOKEN_TIMEOUT);
      const data = JSON.parse(message);
      log.debug("RECV:", data);
      if (data.error) {
        throw new Error(`Error from botBrains: ${JSON.stringify(data)}`);
      }

      if (options.onData) {
        options.onData({ data, cancel, headers: response.headers });
      }
    });

    status = "succeeded";
  } catch (error: any) {
    // if a cancellation is performed
    // do not throw an exception
    // otherwise the content will be overwritten.
    if (hasCancel) {
      status = "cancelled";
      if (options.onError) {
        options.onError({ error: error, signal: controller.signal });
      }
      return;
    }
    status = "failed";
    if (options.onError) {
      options.onError({ error: error, signal: controller.signal });
    }
    throw error;
  } finally {
    clearTimeout(headersTimer);
    clearTimeout(tokenTimer);
    if (options.onEnd) {
      options.onEnd(status);
    }
  }
  return cancel;
}
